/**
 * "Magazine" — designer Cover 3.
 * Masthead composition: kicker, oversized serif headline, full-width
 * hairline rule, then a single meta row (subtitle left, inline stats
 * right). Big "СВАЙП →" arrow bottom-right over the bottom scrim.
 *
 * Layout reflows: cleared texts are skipped; when subtitle + stats don't
 * fit one row the stats drop to a second right-aligned row, shrinking
 * further if even a full row is too narrow for them.
 */

import { SLIDE_W, SLIDE_H, type CarouselTemplate, type CoverSlideData, type SlideStyle } from "../types";
import {
  drawDesignerBackdrop, drawMockScrim, drawWatermark,
  drawTrackedText, drawTrackedRight, measureTracked,
  splitStatValue, drawLongArrow, drawSerifHeadline,
} from "../shared";
import { NUMBER_FONT } from "../fonts";
import { fieldDiary } from "./field-diary";

function drawCover(ctx: CanvasRenderingContext2D, data: CoverSlideData, style: SlideStyle): void {
  const W = SLIDE_W, H = SLIDE_H, PAD = 64;
  const DISPLAY = style.fonts.display;
  const BODY = style.fonts.body;

  ctx.clearRect(0, 0, W, H);
  const textOnLight = drawDesignerBackdrop(ctx, data);
  drawMockScrim(ctx, 650, true, !textOnLight);
  drawMockScrim(ctx, 300, false, !textOnLight);

  const rgb = textOnLight ? "28,36,32" : "255,255,255";
  const solid = textOnLight ? "#1c2420" : "#ffffff";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";

  // ── Kicker (optional) ──
  const kicker = data.kickerText.trim();
  const kickerSize = 34 * (data.kickerScale ?? 1);
  if (kicker) {
    ctx.font = `500 ${kickerSize}px ${BODY}`;
    ctx.fillStyle = `rgba(${rgb},0.52)`;
    drawTrackedText(ctx, kicker.toUpperCase(), PAD, 58 + kickerSize, 10 * (data.kickerScale ?? 1));
  }

  // ── Headline (design size 375 at the default 140 slider position) ──
  const size = 375 * (data.headlineFontSize / 140);
  const capTop = kicker ? 58 + kickerSize + 55 : 84;
  ctx.fillStyle = solid;
  const lastBaseline = drawSerifHeadline(ctx, data.headlineTitle, PAD, 0, W - PAD * 2, size, DISPLAY, { capTop });

  const subtitle = data.subtitle.trim();
  const filledStats = data.stats.filter((s) => s.value.trim() !== "");

  // ── Hairline rule (kept while there is anything under it) ──
  const ruleY = lastBaseline + 21;
  if (subtitle || filledStats.length > 0) {
    ctx.fillStyle = `rgba(${rgb},0.2)`;
    ctx.fillRect(PAD, ruleY, W - PAD * 2, 1);
  }

  // ── Meta row: subtitle left, stats right ──
  const metaBase = ruleY + 60;
  let subtitleEnd = PAD;
  if (subtitle) {
    const s = data.subtitleScale ?? 1;
    ctx.font = `400 ${32 * s}px ${BODY}`;
    ctx.fillStyle = `rgba(${rgb},0.72)`;
    subtitleEnd = PAD + measureTracked(ctx, subtitle.toUpperCase(), 4 * s);
    drawTrackedText(ctx, subtitle.toUpperCase(), PAD, metaBase, 4 * s);
  }

  // Inline stats, right-aligned: «22 ВИДА · 7 ЧАСОВ» with serif numbers.
  if (filledStats.length > 0) {
    const parts = filledStats.map((s) => splitStatValue(s.value));
    const DOT_GAP = 18;

    const measureAll = (k: number): number => {
      let total = 0;
      parts.forEach((p, i) => {
        if (i > 0) total += DOT_GAP * 2 * k + 4;
        if (p.num) {
          ctx.font = `400 ${40 * k}px ${NUMBER_FONT}`;
          total += ctx.measureText(p.num).width + (p.label ? 12 * k : 0);
        }
        ctx.font = `400 ${30 * k}px ${BODY}`;
        total += measureTracked(ctx, p.label.toUpperCase(), 4 * k);
      });
      return total;
    };

    // Shrink until the stats at least fit a full row on their own.
    let st = data.statsScale ?? 1;
    let total = measureAll(st);
    while (total > W - PAD * 2 && st > 0.6) {
      st = Math.max(0.6, st * ((W - PAD * 2) / total) * 0.98);
      total = measureAll(st);
    }

    let x = W - PAD - total;
    // Long subtitles: drop the stats to a second row instead of colliding.
    const statsBase = subtitle && x < subtitleEnd + 30 ? metaBase + 58 : metaBase;
    parts.forEach((p, i) => {
      if (i > 0) {
        ctx.fillStyle = `rgba(${rgb},0.3)`;
        ctx.beginPath();
        ctx.arc(x + DOT_GAP * st, statsBase - 11, 2.5, 0, Math.PI * 2);
        ctx.fill();
        x += DOT_GAP * 2 * st + 4;
      }
      if (p.num) {
        ctx.font = `400 ${40 * st}px ${NUMBER_FONT}`;
        ctx.fillStyle = `rgba(${rgb},0.7)`;
        ctx.fillText(p.num, x, statsBase);
        x += ctx.measureText(p.num).width + (p.label ? 12 * st : 0);
      }
      if (p.label) {
        ctx.font = `400 ${30 * st}px ${BODY}`;
        ctx.fillStyle = `rgba(${rgb},0.58)`;
        x = drawTrackedText(ctx, p.label.toUpperCase(), x, statsBase, 4 * st);
      }
    });
  }

  // ── Big swipe cue bottom-right ──
  ctx.font = `500 26px ${BODY}`;
  ctx.fillStyle = `rgba(${rgb},0.38)`;
  drawTrackedRight(ctx, (data.swipeText?.trim() || "СВАЙП").toUpperCase(), 889, 1248, 8);
  ctx.strokeStyle = solid;
  drawLongArrow(ctx, 1002, 1240, 73, 9, 26);

  drawWatermark(ctx, style, textOnLight);
}

export const magazine: CarouselTemplate = {
  id: "magazine",
  name: "Magazine",
  premium: false,
  drawCover,
  drawSpecies: fieldDiary.drawSpecies,
  drawClosing: fieldDiary.drawClosing,
  // Species/closing draw functions are literally reused from Field Diary,
  // so its overlay positions apply unchanged. Cover has its own bespoke
  // layout here — not yet click-to-edit.
  useTextOverlay: { species: true, closing: true },
};
